import { useEffect, useRef, useState } from "react";
import { Avatar, IconButton, Menu, useTheme } from "@mui/material";
import { useDispatch } from "react-redux";

import { useRequestState } from "@app/hooks";
import { selectMyProfileState } from "@app/modules/my-profile/selector";
import { myProfile } from "@app/modules/my-profile/slice";
import { imageSource } from "@app/constants";

import { UserMenuHeader } from "./user-menu-header";
import { UserMenuItems } from "./user-menu-items";

interface UserMenuProps {
  userName: string;
  onEditProfile: () => void;
  onMyOrders: () => void;
  onLogout: () => void;
}

const UserProfileMenu = ({
  userName,
  onEditProfile,
  onMyOrders,
  onLogout,
}: UserMenuProps) => {
  const handleDoubleCallMyProfilesApi = useRef(false);
  const theme = useTheme();
  const dispatch = useDispatch();

  const [openMenuBar, setOpenMenuBar] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const open = Boolean(openMenuBar);
  const handleClose = () => {
    setOpenMenuBar(null);
  };

  useEffect(() => {
    if (handleDoubleCallMyProfilesApi.current) return;
    handleDoubleCallMyProfilesApi.current = true;
    dispatch(myProfile());
  }, [dispatch]);

  const { data } = useRequestState({
    stateSelector: selectMyProfileState,
  });

  return (
    <>
      <IconButton
        onClick={(e) => setOpenMenuBar(e.currentTarget)}
        disableRipple
        sx={{
          bgcolor: theme.palette.common.white,
          width: 40,
          height: 40,
        }}
      >
        <Avatar
          src={data?.profileImage || ""}
          sx={{
            bgcolor: theme.palette.primary.main,
            color: theme.palette.common.white,
          }}
        >
          {!data?.profileImage &&
            `${data?.firstName?.[0] || ""}${data?.lastName?.[0] || ""}`.trim()}
        </Avatar>
      </IconButton>

      {open && (
        <img
          src={imageSource.userProfileArrow}
          alt="Arrow"
          loading="lazy"
          style={{
            position: "absolute",
            top: 58,
            right: 75,
            width: 20,
            height: 20,
            zIndex: 1300,
          }}
        />
      )}

      <Menu
        anchorEl={openMenuBar}
        open={open}
        onClose={() => setOpenMenuBar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ marginTop: 2, marginLeft: 5 }}
        PaperProps={{
          sx: {
            p: 0,
            width: 250,
            borderRadius: 1,
            borderWidth:0,
            overflow: "hidden",
            position: "relative",
            mt: 0.6,
            mr: 4,
          },
        }}
      >
        <UserMenuHeader
          profileImage={data?.profileImage}
          userName={`${data?.firstName || ""} ${data?.lastName || ""}`}
        />

        <UserMenuItems
          selectedItem={selectedItem}
          onSelect={setSelectedItem}
          onEditProfile={onEditProfile}
          onMyOrders={onMyOrders}
          handleClose={handleClose}
          onLogout={() => {
            setOpenMenuBar(null);
            onLogout();
          }}
        />
      </Menu>
    </>
  );
};

export { UserProfileMenu };
